import React, { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import { Info, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import NestingDiagram from "./NestingDiagram";

// ── helpers (mirrors CreateOrderPage step-3 logic) ────────────────────────────

const getPricePerSqMeter = (item) => {
  if (item.applied_rate_uom === "SQM") return item.applied_rate;
  if (item.applied_rate_uom === "MTR")
    return item.applied_rate / ((item.product_width_mm || 1000) / 1000);
  return item.applied_rate;
};

const getMaterialDisplay = (item) => {
  const uom = (item.applied_rate_uom || "").toUpperCase();
  const qty = item.quantity || 1;
  const sheets = item.nesting_sheets_used ?? 1;
  const sheetLen = item.product_length_mm || item.nesting_length_used_mm || 1000;
  const netMtr = (sheets * sheetLen) / 1000;
  const perQty = `${((item.shapes_area_sqmm || 0) / qty / 1_000_000).toFixed(4)} sq.m`;

  if (uom === "MTR") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/mtr` };
  if (uom === "SHEET") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/sheet` };
  if (uom === "PCS" || uom === "ROL") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/pc` };
  return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/sq.m` };
};

// ── component ─────────────────────────────────────────────────────────────────

export default function OrderItemDetail({ item, index }) {
  const { perQty, net, rate } = getMaterialDisplay(item);
  const matCostPerPiece = item.quantity > 0 ? item.material_cost / item.quantity : 0;
  const cutRatePerPc = item.cutting_rate_per_running_mm || 0;
  const noCut = cutRatePerPc === 0;

  // Defer heavy diagram render so the popup paints immediately even when
  // an item has thousands of placements.
  const [showDiagram, setShowDiagram] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setShowDiagram(true); };
    const ric = window.requestIdleCallback;
    const id = ric
      ? ric(reveal, { timeout: 300 })
      : setTimeout(reveal, 80);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/designs/${item.design_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || "design";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("File not available for download");
    }
  };

  return (
    <div className="space-y-3" data-testid={`order-item-detail-${index}`}>

      {/* ARTICLE DETAILS */}
      <div className="border border-slate-200 bg-white">
        <div className="bg-slate-900 text-white px-4 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider">Article Details</h3>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <h4 className="text-base font-bold text-slate-900">{item.product_name}</h4>
            <Badge variant="outline" className="rounded-none text-[10px] uppercase">{item.material_type || "ROLL"}</Badge>
            {item.filename && (
              <button
                onClick={handleDownload}
                className="ml-auto flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-400 px-2.5 py-1 transition-colors"
                title={`Download ${item.filename}`}
              >
                <Download size={13} />
                <span className="font-mono">{item.filename}</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Width:</span>
              <span className="font-mono font-medium">{item.product_width_mm?.toLocaleString()} mm</span>
            </div>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Price:</span>
              <span className="font-mono font-semibold">
                ₹{item.applied_rate}/{item.applied_rate_uom === "SQM" ? "sq.m" : item.applied_rate_uom === "MTR" ? "mtr" : item.applied_rate_uom?.toLowerCase() || "unit"}
              </span>
              {item.applied_rate_uom !== "SQM" && (
                <span className="text-slate-500 font-mono text-xs">(₹{getPricePerSqMeter(item).toFixed(2)}/sq.m)</span>
              )}
            </div>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Qty:</span>
              <span className="font-mono font-medium">{item.quantity} pcs</span>
            </div>
            <div className="h-4 w-px bg-slate-200" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 cursor-help">
                    <span className="text-slate-500 text-xs">Pcs/Sq.M:</span>
                    <span className="font-mono font-medium">{item.pcs_per_sqm ? Math.round(item.pcs_per_sqm) : "—"}</span>
                    <Info size={13} className="text-slate-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  <p className="font-semibold mb-1">Pieces per Square Meter</p>
                  <p>How many pieces of this design can be cut from 1 square meter of material. Higher value = more efficient material usage.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Wastage:</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-mono font-medium text-red-600 cursor-help">
                      {item.wastage_pct?.toFixed(1)}%
                      {item.nesting_sheets_used > 1 && (
                        <span className="text-[10px] text-slate-400 ml-1">/{item.nesting_sheets_used}sh</span>
                      )}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {item.nesting_sheets_used > 1
                      ? <p>Combined wastage across all <strong>{item.nesting_sheets_used} sheets</strong>.</p>
                      : <p>Material wastage — unused area after nesting all pieces on the sheet.</p>
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      {/* COST BREAKDOWN */}
      <div className="border border-slate-200 bg-white">
        <div className="bg-slate-900 text-white px-4 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider">Cost Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase">Component</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Comp. Qty</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Per Piece</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Net Total</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Rate</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Cost/Piece</TableHead>
                <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Total Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Material row */}
              <TableRow>
                <TableCell className="text-sm">
                  <span className="font-medium">{item.component_name || item.filename}</span>
                  <span className="text-[10px] text-slate-400 ml-1">({item.product_name})</span>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{item.quantity}</TableCell>
                <TableCell className="text-right font-mono text-sm">{perQty}</TableCell>
                <TableCell className="text-right font-mono text-sm">{net}</TableCell>
                <TableCell className="text-right font-mono text-sm">{rate}</TableCell>
                <TableCell className="text-right font-mono text-sm">₹{matCostPerPiece.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-medium">
                  ₹{item.material_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              </TableRow>

              {/* Cutting row */}
              <TableRow className={noCut ? "opacity-50" : ""}>
                <TableCell className="text-sm">
                  <span className="font-medium">{item.component_name || item.filename}</span>
                  <span className="text-[10px] text-slate-400 ml-1">(Cutting)</span>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{item.quantity}</TableCell>
                <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}/pc`}</TableCell>
                <TableCell className="text-right font-mono text-sm">{item.quantity} pcs</TableCell>
                <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}/pc`}</TableCell>
                <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}`}</TableCell>
                <TableCell className="text-right font-mono text-sm font-medium">
                  ₹{item.cutting_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              </TableRow>

              {/* Total row */}
              <TableRow className="bg-slate-900 text-white">
                <TableCell className="font-bold uppercase tracking-wider text-white text-sm" colSpan={5}>Total Order Cost</TableCell>
                <TableCell className="text-right font-mono font-bold text-white text-sm">
                  ₹{item.quantity > 0 ? ((item.item_total || 0) / item.quantity).toFixed(2) : "0.00"}
                </TableCell>
                <TableCell className="text-right font-mono font-bold text-orange-400 text-base">
                  ₹{(item.item_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={7} className="text-xs text-slate-500 italic">
                  * GST will be Charged Extra as Applicable
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      {/* NESTING LAYOUT */}
      <div className="border border-slate-200 bg-white">
        <div className="bg-slate-900 text-white px-4 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider">Nesting Layout</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
          <div className="lg:col-span-2 p-4">
            {showDiagram ? (
              <NestingDiagram
                placements={item.placements || []}
                fallbackShape={item.shape_points}
                rollWidth={item.product_width_mm || 1000}
                rollLength={item.nesting_length_used_mm || 1000}
                materialType={item.material_type}
                totalSheets={item.nesting_sheets_used || 1}
              />
            ) : (
              <div className="h-[360px] flex items-center justify-center bg-slate-50 border border-dashed border-slate-200 text-slate-400 text-xs uppercase tracking-wider">
                Loading nesting layout…
              </div>
            )}
            <p className="text-center text-[10px] text-slate-400 mt-2">
              Width: {item.product_width_mm?.toLocaleString()} mm | Length: {item.nesting_length_used_mm?.toLocaleString()} mm
              {item.nesting_sheets_used > 1 && ` | ${item.nesting_sheets_used} sheets`}
            </p>
          </div>
          <div className="p-4 space-y-2 bg-slate-50 text-sm">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-700 border-b pb-1.5">Dimensions</h4>
            <div className="space-y-1.5">
              {[
                ["Material Width", `${item.product_width_mm?.toLocaleString()} mm`],
                ["Sheet Length", `${item.nesting_length_used_mm?.toLocaleString()} mm`],
                ["Sheets Used", `${item.nesting_sheets_used ?? 1}`],
                ["Piece Area", `${((item.shapes_area_sqmm || 0) / 1_000_000).toFixed(4)} sq.m`],
                ["Material Consumed", `${((item.material_consumed_sqmm || 0) / 1_000_000).toFixed(4)} sq.m`],
                ["Pieces Count", item.pieces_count?.toLocaleString() ?? "—"],
                ["Cutting Rate", cutRatePerPc > 0 ? `₹${cutRatePerPc.toFixed(2)}/pc` : "—"],
              ].map(([label, val]) => {
                const isSheets = label === "Sheets Used";
                const sheetsVal = item.nesting_sheets_used ?? 1;
                return (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-slate-500 text-xs">{label}</span>
                    <span className={`font-mono font-medium text-xs ${isSheets && sheetsVal > 1 ? "bg-orange-100 text-orange-700 px-1.5 py-0.5" : ""}`}>
                      {val}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
